import { useEffect, useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import FilterTransaction from 'components/Global/TransactionFilter';
import { ReactComponent as WithDriverIcon } from 'icons/with-driver-filled-icon.svg';
import { PaginationTable, TransactionFilterCar } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import { changeOrderStatus, fetchOrderById, fetchWithDriverOrder } from 'features/orders/actions';
import { withoutDriverColumn } from 'utils/order/order-column';
import useCreateTableData from 'utils/useCreateTableData';
import clsx from 'clsx';
import StatusButton from 'components/Global/Table/StatusButton';
import ActionButtons from 'components/Global/Table/ActionButtons';
import { resetDetailData, saveDetailData } from 'features/orders/detailOrderSlice';
import { getVehicleById } from 'features/vehicle/action';
import { fetchCustomerInfo } from 'features/user/actions';
import { useAppContext } from 'components/Context/AppContext';
import { downloadInvoice } from 'features/invoice/actions';
import EmptyState from 'components/Global/EmptyState';
import SearchBar from 'components/Global/TableWrapper/SearchBar';
import useDebounce from 'utils/useDebounce';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { setNotificationPayload } from 'features/notification/notifSlice';
import { ReactComponent as ChevronUpBlack } from 'icons/chevron-up-black.svg';
import { ReactComponent as ChevronDownBlack } from 'icons/chevron-down-black.svg';
import { ReactComponent as ChevronUpBlue } from 'icons/chevron-up-blue.svg';
import { ReactComponent as ChevronDownBlue } from 'icons/chevron-down-blue.svg';
import { ReactComponent as ChevronUpGray } from 'icons/chevron-up-gray.svg';
import { ReactComponent as ChevronDownGray } from 'icons/chevron-down-gray.svg';
import { ReactComponent as Star } from 'icons/star.svg';
import { getAllRentalLocation } from 'features/rental-location/actions';
import ButtonAdd from 'components/Global/ButtonAdd';
import { getApprovalSettings, updateApprovalOrder } from 'features/approvals/actions';
import { resetSelectedVehicle } from 'features/vehicle/vehicleSlice';
import { checkPermission } from 'utils/functionality';
import { resetSelectedOrderAddon } from 'features/addon/orderAddonsSlice';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { trackEvent } from 'utils/analytics';

const FILTER_DATA = [
  {
    id: 'RECONFIRMATION',
    name: 'Re-Konfirmasi',
    value: false,
  },
  {
    id: 'CHECKOUT',
    name: 'Konfirmasi Order',
    value: false,
  },
  {
    id: 'PAID',
    name: 'Belum Diproses',
    value: false,
  },
  {
    id: 'COMPLETED',
    name: 'Sedang Berjalan',
    value: false,
  },
  {
    id: 'FINISHED',
    name: 'Selesai Sewa',
    value: false,
  },
  {
    id: 'CANCELLED',
    name: 'Batal Sewa',
    value: false,
  },
  {
    id: 'REJECTED',
    name: 'Order Ditolak',
    value: false,
  },
];

const WithDriver = () => {
  const LOCATIONS_DATA = useSelector((state) => state.rentalLocation.data);
  const [filterStatusList, setFilterStatusList] = useState(FILTER_DATA);
  const [filterLocationList, setFilterLocationList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { setShowConfirmation, showToast, setShowSendNotification } = useAppContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: withDriverOrder, status } = useSelector((state) => state.withDriverOrder);
  const approvalData = useSelector((state) => state.approvals.approvalSetting);
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const pageNumber = (page, idx) => (page > 1 ? (page - 1) * 10 + idx : idx);
  const [searchParams] = useSearchParams();
  const filteredStatus = searchParams.get('status');
  const filteredLocation = searchParams.get('locationId');
  const [search, setSearch] = useState('');
  const searchValueDebounce = useDebounce(search, 1000);
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [vehicleIdFilter, setVehicleIdFilter] = useState([]);
  const [sortBy, setSortBy] = useState({ sortValue: 'created_at', count: 0 });
  const [orderSeq, setOrderSeq] = useState('DESC');

  const user = useSelector((state) => state.user.profile);

  const isDriverDeleteApproval = useFliptBoolean('FLAG_FE_DELETE_DRIVER_APPROVAL', false, user.id || 'anonymous');

  useEffect(() => {
    dispatch(getAllRentalLocation());
    dispatch(getApprovalSettings({ key: 'delete-order', limit: 5 }));
  }, []);

  useEffect(() => {
    if (filteredStatus?.toLowerCase() !== 'all') {
      setFilterStatusList((prev) =>
        prev.map((item) =>
          filteredStatus?.split(',').find((status) => status === item.id)
            ? { ...item, value: true }
            : { ...item, value: false },
        ),
      );
    } else if (filteredStatus?.toLowerCase() == 'all') {
      setFilterStatusList(FILTER_DATA);
    }
  }, [filteredStatus]);

  useEffect(() => {
    if (filteredLocation?.toLowerCase() !== 'all') {
      setFilterLocationList(() =>
        LOCATIONS_DATA.map((item) =>
          filteredLocation?.split(',').find((location) => location == item.id)
            ? { ...item, value: true }
            : { ...item, value: false },
        ),
      );
    } else if (filteredLocation?.toLowerCase() == 'all') {
      setFilterLocationList(
        LOCATIONS_DATA.map((item) =>
          item.name.toLowerCase() === 'bali' ? { ...item, value: true } : { ...item, value: false },
        ),
      );
    }
  }, [filteredLocation, LOCATIONS_DATA]);

  useEffect(() => {
    if (filteredStatus || filteredLocation || searchValueDebounce) {
      setCurrentPage(1);
      dispatch(
        fetchWithDriverOrder({
          page: 1,
          order: orderSeq,
          orderBy: sortBy.sortValue,
          status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
          locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
          user_name: searchValueDebounce ? searchValueDebounce : undefined,
          startDate: startDateFilter,
          endDate: endDateFilter,
          vehicleId: vehicleIdFilter,
          timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
            ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
            : undefined,
        }),
      );
    }
  }, [filteredStatus, filteredLocation, searchValueDebounce, LOCATIONS_DATA]);

  useEffect(() => {
    dispatch(
      fetchWithDriverOrder({
        page: currentPage,
        order: orderSeq,
        status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
        locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
        user_name: searchValueDebounce ? searchValueDebounce : undefined,
        startDate: startDateFilter,
        endDate: endDateFilter,
        vehicleId: vehicleIdFilter,
        timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
          ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
          : undefined,
      }),
    );
  }, [currentPage]);

  const customOrderHandler = () => {
    navigate('/with-driver/custom-order');
  };

  const handleDetail = (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];

    const filteredOrderById = withDriverOrder.data.filter((item) => item.id === +orderId);
    dispatch(saveDetailData(filteredOrderById[0]));
    dispatch(getVehicleById(filteredOrderById[0].order_detail.vehicle_id));
    dispatch(fetchCustomerInfo(filteredOrderById[0].customer_id));
    navigate({
      pathname: `detail/${filteredOrderById[0].transaction_key}`,
      search: createSearchParams({
        customerId: filteredOrderById[0].customer_id,
      }).toString(),
    });
  };

  // const handleKeyPress = useCallback((e) => {
  //   const { keyCode } = e;
  //   if (keyCode === 8 || keyCode === 32 || (keyCode >= 48 && keyCode <= 90)) {
  //     setIsLoading(true);
  //     return;
  //   }
  // }, []);

  // useEffect(() => {
  //   window.addEventListener('keydown', handleKeyPress);

  //   return () => window.removeEventListener('keydown', handleKeyPress);
  // }, [handleKeyPress]);

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     setIsLoading(false);
  //   }, 500);
  //   return () => {
  //     clearTimeout(timeout);
  //   };
  // }, [searchValueDebounce]);

  const handleDelete = async (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];
    if (!orderId) return;

    let orderDetail = {};

    const filteredOrderById = withDriverOrder.data.find((item) => item.id === +orderId);

    try {
      const getOrderDetail = await dispatch(fetchOrderById(filteredOrderById.transaction_key)).unwrap();
      orderDetail = { ...getOrderDetail };
    } catch (error) {
      return showToast({ type: 'error', message: 'Gagal Mendapatkan Detail Order' });
    }

    const payload = {
      reference_id: isDriverDeleteApproval ? orderId : +orderId,
      reference_type: 'order',
      approval_setting_id: approvalData.data[0].id,
      data: orderDetail,
    };

    setShowConfirmation({
      message: 'Apakah anda yakin ingin menghapus data?',
      show: true,
      onClick: async () => {
        try {
          trackEvent('approval_decided', {
            order_id: filteredOrderById.transaction_key,
            decision: 'delete',
            source: 'with_driver_transaction_list',
          });
          await dispatch(updateApprovalOrder(payload)).unwrap();
          showToast({ type: 'success', message: 'Berhasil hapus data dan menunggu konfirmasi' });
        } catch (error) {
          showToast({ type: 'error', message: 'Gagal Menghapus Data Order' });
          // eslint-disable-next-line no-console
          console.log(error);
        } finally {
          dispatch(
            fetchWithDriverOrder({
              page: 1,
              order: orderSeq,
              status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
              locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
              user_name: searchValueDebounce ? searchValueDebounce : undefined,
              startDate: startDateFilter,
              endDate: endDateFilter,
              vehicleId: vehicleIdFilter,
              timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
                ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
                : undefined,
            }),
          );
        }
      },
    });
  };

  const handleUpdate = (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];

    const filteredOrderById = withDriverOrder.data.filter((item) => item.id === +orderId);
    dispatch(resetSelectedVehicle());
    dispatch(resetSelectedOrderAddon());
    dispatch(resetDetailData());
    navigate({
      pathname: `detail/${filteredOrderById[0].transaction_key}`,
      search: createSearchParams({
        customerId: filteredOrderById[0].customer_id,
        update: true,
      }).toString(),
    });
  };

  const handleSendNotification = (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];

    const filteredOrderById = withDriverOrder.data.filter((item) => item.id === +orderId);
    setShowSendNotification(true);
    dispatch(
      setNotificationPayload({
        receiver_id: filteredOrderById[0].customer_id,
        type: 'reminder',
        payload: { email: filteredOrderById[0].email },
      }),
    );
  };

  const handleDownloadInvoice = async (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];

    const filteredOrderById = withDriverOrder.data.filter((item) => item.id === +orderId);
    const payload = {
      transactionKey: filteredOrderById[0].transaction_key,
      orderKey: filteredOrderById[0].order_key,
    };

    try {
      await dispatch(downloadInvoice(payload)).unwrap();
    } catch (error) {
      showToast({ type: 'error', message: 'Gagal download invoice!' });
    }
  };

  const handleSuccess = (e) => {
    const orderId = e.target.parentElement.parentElement.dataset['key'];

    const filteredOrderById = withDriverOrder.data.filter((item) => item.id === +orderId);
    // setShowConfirmation(true);

    setShowConfirmation({
      message: 'Apakah anda yakin ingin menyelesaikan order sewa',
      show: true,
      onClick: async () => {
        if (filteredOrderById[0].order_status !== 'COMPLETED') return;

        const payload = {
          prevStatus: 'COMPLETED',
          nextStatus: 'FINISHED',
          transaction_key: filteredOrderById[0].transaction_key,
        };

        try {
          trackEvent('order_status_advanced', {
            order_id: filteredOrderById[0].transaction_key,
            to_status: 'FINISHED',
            source: 'with_driver_transaction_list',
            via_approval: false,
          });
          await dispatch(changeOrderStatus(payload)).unwrap();
          showToast({ type: 'success', message: 'Transaksi Berhasil Diselesaikan' });
        } catch (err) {
          showToast({ type: 'error', message: 'Transaksi Gagal Diselesaikan' });
        } finally {
          dispatch(
            fetchWithDriverOrder({
              page: currentPage,
              order: orderSeq,
              orderBy: sortBy.sortValue,
              status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
              locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
              user_name: searchValueDebounce ? searchValueDebounce : undefined,
              startDate: startDateFilter,
              endDate: endDateFilter,
              vehicleId: vehicleIdFilter,
              timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
                ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
                : undefined,
            }),
          );
          setShowConfirmation(false);
        }
      },
    });
  };

  const handleFilterOrder = (filters) => {
    setStartDateFilter(filters.start_date);
    setEndDateFilter(filters.end_date);
    setVehicleIdFilter(filters.vehicle_id);

    setCurrentPage(1);
    dispatch(
      fetchWithDriverOrder({
        page: 1,
        order: orderSeq,
        orderBy: sortBy.sortValue,
        status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
        locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
        user_name: searchValueDebounce ? searchValueDebounce : undefined,
        startDate: filters.start_date,
        endDate: filters.end_date,
        vehicleId: filters.vehicle_id,
        timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
          ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
          : undefined,
      }),
    );
  };

  const handleResetFilterOrder = () => {
    setStartDateFilter('');
    setEndDateFilter('');
    setVehicleIdFilter('');

    if (search) {
      setSearch('');
    } else {
      setCurrentPage(1);
      dispatch(
        fetchWithDriverOrder({
          page: 1,
          status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
          locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
          timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
            ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
            : undefined,
        }),
      );
    }
  };

  const sortByHandler = (sortValue) => {
    if (sortValue === sortBy.sortValue) {
      if (sortBy.count === 1) {
        setSortBy({ sortValue, count: sortBy.count + 1 });
        setOrderSeq('DESC');
        dispatch(
          fetchWithDriverOrder({
            page: currentPage,
            order: 'DESC',
            orderBy: sortValue,
            status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
            locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
            user_name: searchValueDebounce ? searchValueDebounce : undefined,
            startDate: startDateFilter,
            endDate: endDateFilter,
            vehicleId: vehicleIdFilter,
            timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
              ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
              : undefined,
          }),
        );
      } else if (sortBy.count === 2) {
        setSortBy({ sortValue: '', count: 0 });
        setOrderSeq('DESC');
        dispatch(
          fetchWithDriverOrder({
            page: currentPage,
            order: 'DESC',
            orderBy: 'created_at',
            status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
            locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
            user_name: searchValueDebounce ? searchValueDebounce : undefined,
            startDate: startDateFilter,
            endDate: endDateFilter,
            vehicleId: vehicleIdFilter,
            timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
              ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
              : undefined,
          }),
        );
      }
    } else {
      setSortBy({ sortValue, count: 1 });
      setOrderSeq('ASC');
      dispatch(
        fetchWithDriverOrder({
          page: currentPage,
          order: 'ASC',
          orderBy: sortValue,
          status: filteredStatus?.toLowerCase() !== 'all' ? filteredStatus?.split(',') : undefined,
          locationId: filteredLocation?.toLowerCase() !== 'all' ? filteredLocation?.split(',') : undefined,
          user_name: searchValueDebounce ? searchValueDebounce : undefined,
          startDate: startDateFilter,
          endDate: endDateFilter,
          vehicleId: vehicleIdFilter,
          timeZoneIdentifier: LOCATIONS_DATA?.find((item) => item.id == filteredLocation)
            ? LOCATIONS_DATA.find((item) => item.id == filteredLocation).time_zone_identifier
            : undefined,
        }),
      );
    }
  };

  const { data } = useCreateTableData(withDriverOrder.data);

  const rows = 10 - data.length;

  const COLUMN = withoutDriverColumn;
  const stars = Array(5).fill(0);

  return (
    <div className="transaction-without-driver">
      <ButtonAdd onClick={customOrderHandler} label="Custom Order" />

      <TransactionFilterCar onReset={handleResetFilterOrder} onApply={handleFilterOrder} />

      <div className="transaction-without-driver__wrapper">
        <div className="transaction-without-driver__header">
          <div className="transaction-without-driver__title">
            <WithDriverIcon fill="#009EF7" width="25px" height="25px" />
            <h2>Dengan Supir</h2>
          </div>
          <div className="transaction-without-driver__filter">
            <FilterTransaction
              selected={filterLocationList}
              setPage={setCurrentPage}
              className="filter-location"
              placeholder="Filter Lokasi Sewa"
              params={filteredLocation}
              paramName="locationId"
              multiple={false}
            />
            <FilterTransaction
              selected={filterStatusList}
              setPage={setCurrentPage}
              className="filter-status"
              placeholder="Filter Status Sewa"
              params={filteredStatus}
              paramName="status"
            />
            <SearchBar
              type="text"
              value={search}
              placeholder="Search by Name"
              onChange={setSearch}
              style={{ marginLeft: 8 }}
            />
          </div>
        </div>

        {status === 'loading' ? (
          <LoadingSpinner />
        ) : data?.length === 0 && status !== 'loading' ? (
          <EmptyState />
        ) : (
          <>
            <div className={clsx('table')}>
              <table className="table-wrapper">
                <thead className="table-head">
                  <tr>
                    {COLUMN.map((item, i) => {
                      if (item.header === 'Nama Lengkap') {
                        return (
                          <th key={i}>
                            <div className="sorted-wrapper" onClick={() => sortByHandler('customer_name')}>
                              {item.header}
                              <div className="chevrons">
                                {sortBy.count === 0 ? (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                ) : sortBy.count === 1 && sortBy.sortValue === 'customer_name' ? (
                                  <>
                                    <ChevronUpBlue />
                                    <ChevronDownGray />
                                  </>
                                ) : sortBy.count === 2 && sortBy.sortValue === 'customer_name' ? (
                                  <>
                                    <ChevronUpGray />
                                    <ChevronDownBlue />
                                  </>
                                ) : (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                )}
                              </div>
                            </div>
                          </th>
                        );
                      } else if (item.header === 'Mulai Sewa') {
                        return (
                          <th key={i}>
                            <div className="sorted-wrapper" onClick={() => sortByHandler('start_date')}>
                              {item.header}
                              <div className="chevrons">
                                {sortBy.count === 0 ? (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                ) : sortBy.count === 1 && sortBy.sortValue === 'start_date' ? (
                                  <>
                                    <ChevronUpBlue />
                                    <ChevronDownGray />
                                  </>
                                ) : sortBy.count === 2 && sortBy.sortValue === 'start_date' ? (
                                  <>
                                    <ChevronUpGray />
                                    <ChevronDownBlue />
                                  </>
                                ) : (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                )}
                              </div>
                            </div>
                          </th>
                        );
                      } else if (item.header === 'Selesai Sewa') {
                        return (
                          <th key={i}>
                            <div className="sorted-wrapper" onClick={() => sortByHandler('end_date')}>
                              {item.header}
                              <div className="chevrons">
                                {sortBy.count === 0 ? (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                ) : sortBy.count === 1 && sortBy.sortValue === 'end_date' ? (
                                  <>
                                    <ChevronUpBlue />
                                    <ChevronDownGray />
                                  </>
                                ) : sortBy.count === 2 && sortBy.sortValue === 'end_date' ? (
                                  <>
                                    <ChevronUpGray />
                                    <ChevronDownBlue />
                                  </>
                                ) : (
                                  <>
                                    <ChevronUpBlack />
                                    <ChevronDownBlack />
                                  </>
                                )}
                              </div>
                            </div>
                          </th>
                        );
                      } else {
                        return <th key={i}>{item.header}</th>;
                      }
                    })}
                    <th className="action-col">Action</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {data.length > 0 &&
                    data.map((item, no) => (
                      <tr key={no}>
                        {COLUMN.map((col, idx) => {
                          if (col.value === 'no') {
                            return (
                              <td
                                key={idx}
                                data-type="no"
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                              >
                                {pageNumber(currentPage, no + 1)}
                              </td>
                            );
                          } else if (col.value === 'status') {
                            return (
                              <td
                                key={idx}
                                data-type={'status'}
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                              >
                                {<StatusButton status={item[col.value]} />}
                              </td>
                            );
                          } else if (col.value === 'type') {
                            return (
                              <td
                                key={idx}
                                className={`pre-white-space address_details ${
                                  col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''
                                }`}
                              >
                                {<StatusButton status={item[col.value].toLowerCase()} />}
                              </td>
                            );
                          } else if (col.value === 'platform') {
                            return (
                              <td
                                key={idx}
                                className={`pre-white-space address_details ${
                                  col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''
                                }`}
                                style={{ textTransform: 'capitalize' }}
                              >
                                {item[col.value]}
                              </td>
                            );
                          } else if (col.value === 'rating') {
                            return (
                              <td key={idx}>
                                <div className="rating-wrapper">
                                  {stars.map((_, index) => {
                                    return <Star key={index} color={item[col.value] > index ? '#f1a33a' : '#edebeb'} />;
                                  })}
                                </div>
                              </td>
                            );
                          } else {
                            return (
                              <td
                                key={idx}
                                data-type={col.value}
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                              >
                                {item[col.value]}
                              </td>
                            );
                          }
                        })}
                        <td className="btn-action" data-type="actionButtons" data-key={!item.id ? item.code : item.id}>
                          {(item.status === 'paid' || item.status === 'completed' || item.status === 'finished') && (
                            <ActionButtons downloadInvoice={handleDownloadInvoice} />
                          )}
                          <ActionButtons detail={handleDetail} />
                          {item.status !== 'completed' && !item.order_approval_status && (
                            <ActionButtons del={handleDelete} />
                          )}
                          {item.status === 'completed' && (
                            <ActionButtons success={handleSuccess} sendNotification={handleSendNotification} />
                          )}
                          {checkPermission(offCanvasMenu, currentMenu, 'update') &&
                            item.status === 'paid' &&
                            !item.is_task_active && <ActionButtons edit={handleUpdate} />}
                        </td>
                      </tr>
                    ))}
                  {rows > 0 &&
                    [...Array(rows)].map((_, idx) => (
                      <tr key={idx}>
                        {COLUMN.map((_, idx) => (
                          <td key={idx}></td>
                        ))}
                        <td className="btn-action"></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <PaginationTable
              currentPage={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
              totalCount={withDriverOrder.pagination?.total}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default WithDriver;
